import { type ChangeEvent, type MouseEvent, useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { toast } from "sonner";
import { Info } from "lucide-react";
import {
  MERCHANT_KEY_KEY,
  useCreateMerchantKeyMutation,
  useGenerateRSAKeyMutation,
  useMerchantDetailQuery,
  useMerchantKeyQuery,
} from "@/modules/security/api/useSecurity.ts";
import { Card, CardTitle } from "@/components/ui/card.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import { PageWrapper } from "@/components/PageWrapper.tsx";
import { PageHeader } from "@/components/PageHeader.tsx";
import { useAppConfig } from "@/dataProviders/ConfigDataProvider.tsx";
import { DetailCardRow } from "@/components/DetailCardRow.tsx";
import { Loader } from "@/components/Loader.tsx";
import { Button } from "@/components/ui/button.tsx";
import { formatDate, NOT_AVAILABLE } from "@/lib/utils.ts";
import { DeleteKeyModal } from "@/modules/security/DeleteKeyModal.tsx";
import { FetchingError } from "@/components/FetchingError.tsx";
import { downloadFile, stringToDataUrl } from "@/modules/security/utils.ts";
import { logError } from "@/sentry.ts";
import { CONFIG } from "@/lib/config.ts";

export const Route = createFileRoute("/settings/security/")({
  component: RouteComponent,
});

function RouteComponent() {
  const title = t`Security administration`;
  const { activeApp } = useAppConfig();
  const queryClient = useQueryClient();

  const {
    data: infoData,
    isError: isInfoError,
    isPending: isInfoPending,
  } = useMerchantDetailQuery({ merchantId: activeApp }, { enabled: !!activeApp });
  const {
    data: keyData,
    isError: isKeyError,
    isPending: isKeyPending,
  } = useMerchantKeyQuery({ merchantId: activeApp }, { enabled: !!activeApp });

  const { mutateAsync: generateMutateAsync, isPending: isGeneratePending } = useGenerateRSAKeyMutation({
    merchantId: activeApp,
  });
  const { mutateAsync: createMutateAsync, isPending: isCreatePending } = useCreateMerchantKeyMutation({
    merchantId: activeApp,
  });
  const isKeyMutationPending = isGeneratePending || isCreatePending;

  const inputRef = useRef<HTMLInputElement | null>(null);

  function handleButtonClick(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    if (!inputRef || !inputRef.current || isKeyMutationPending) return;

    inputRef.current.click();
  }

  const uploadKey = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const file = files[0];
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = async () => {
      try {
        if (!reader.result) return;
        const key = btoa(reader.result as string);
        await createMutateAsync({ publicKey: key });
        await queryClient.invalidateQueries({ queryKey: [MERCHANT_KEY_KEY] });
        toast.success(t`RSA key was uploaded successfully.`);
      } catch (e) {
        logError(e);
        toast.error(
          t`Something went wrong. We were unable to upload the RSA key. Please refresh the page and try again.`,
        );
      }
    };
    reader.onerror = () => {
      toast.error(
        t`Something went wrong. We were unable to upload the RSA key. Please refresh the page and try again.`,
      );
    };
  };

  const generateKey = async () => {
    try {
      const { privateKey } = await generateMutateAsync();
      await queryClient.invalidateQueries({ queryKey: [MERCHANT_KEY_KEY] });

      if (!privateKey) throw new Error(t`Invalid private key`);
      downloadFile(stringToDataUrl(privateKey), "private_key.pem");
      toast.success(t`RSA key was generated successfully.`);
    } catch (e) {
      logError(e);
      toast.error(
        t`Something went wrong. We were unable to generate the RSA key. Please refresh the page and try again.`,
      );
    }
  };

  return (
    <PageWrapper>
      <PageHeader breadcrumbs={[{ href: "/", title }]} />
      <div className="flex flex-col grow p-5">
        {(isInfoError || isKeyError) && (
          <FetchingError
            message={t`Something went wrong. We were unable to load the data. Please refresh the page and try again.`}
          />
        )}
        {(isInfoPending || isKeyPending) && <Loader message={t`Loading security settings...`} />}
        {infoData && (
          <Card className={"shadow-none"}>
            <div className={"px-5"}>
              <CardTitle className={"text-xl font-semibold"}>
                <Trans>Security administration</Trans>
              </CardTitle>
            </div>

            <Separator />

            <div className={"px-5"}>
              <p className={"text-sm font-semibold"}>
                <Trans>Basic information</Trans>
              </p>
              <DetailCardRow label={t`Customer ID:`} value={activeApp} copy={false} className={"mt-2"} />
              <div className={"flex flex-col sm:flex-row gap-2 justify-between mt-2 overflow-hidden"}>
                <p className={"text-sm text-muted-foreground"}>
                  <Trans>Redirect URL:</Trans>
                </p>
                {infoData.merchantInfo?.callbackUrl ? (
                  <a className={"underline text-primary text-sm truncate"} href={infoData.merchantInfo?.callbackUrl}>
                    {infoData.merchantInfo?.callbackUrl}
                  </a>
                ) : (
                  <p className={"text-sm "}>{NOT_AVAILABLE}</p>
                )}
              </div>

              {keyData ? (
                <div>
                  <div className={"flex flex-col sm:flex-row gap-2 justify-between items-center mt-2"}>
                    <p className={"text-sm text-muted-foreground"}>
                      <Trans>RSA key:</Trans>
                    </p>
                    <DeleteKeyModal keyId={keyData?.id} />
                  </div>

                  <DetailCardRow
                    label={t`Created at:`}
                    value={formatDate(keyData?.createdAt ?? "")}
                    copy={false}
                    className={"mt-2"}
                  />
                </div>
              ) : (
                <div>
                  <Separator className={"mt-8"} />

                  <div className={"flex flex-col md:flex-row gap-4 items-center mt-8"}>
                    <Info size={16} className={"shrink-0"} />
                    <p className={"text-sm "}>
                      <Trans>
                        The newly generated key is automatically downloaded to the user&#39;s device. Once generated,
                        the key cannot be downloaded again. You can only have one RSA key at a time.
                        <br />
                        To upload your own RSA key, please ensure it is a valid .pem file.{" "}
                        <a
                          href={`${CONFIG.DOCS_URL}#section/Security/Key-pair-generation`}
                          target={"_blank"}
                          rel={"noopener noreferrer"}
                          className={"text-primary underline hover:no-underline"}
                        >
                          Learn more in our documentation.
                        </a>
                      </Trans>
                    </p>
                  </div>
                  <div className={"flex flex-col md:flex-row gap-4 items-center mt-4"}>
                    <Button
                      type={"button"}
                      variant={"default"}
                      onClick={generateKey}
                      disabled={isKeyMutationPending}
                      className={"cursor-pointer"}
                    >
                      <Trans>Generate</Trans>
                    </Button>
                    <Button
                      type={"button"}
                      variant={"outline"}
                      className={"cursor-pointer"}
                      onClick={handleButtonClick}
                      disabled={isKeyMutationPending}
                    >
                      <Trans>Load from file</Trans>
                    </Button>
                    <input ref={inputRef} type="file" className={"invisible"} onChange={uploadKey} />
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </PageWrapper>
  );
}
